import React from 'react';
import { motion } from 'framer-motion';
import { FaFileAlt, FaGavel, FaUserCheck, FaExclamationTriangle, FaHandshake, FaShieldAlt, FaBalanceScale } from 'react-icons/fa';

const TermsConditions = () => {
  const lastUpdated = "January 15, 2025";

  const sections = [
    {
      title: "Acceptance of Terms",
      icon: FaUserCheck,
      content: [
        "By accessing and using In3D.AI, you accept and agree to be bound by these Terms and Conditions.",
        "If you do not agree to these terms, please do not use our services.",
        "We reserve the right to modify these terms at any time, with changes effective immediately upon posting.",
        "Your continued use of the service after changes constitutes acceptance of the new terms."
      ]
    },
    {
      title: "Service Description",
      icon: FaFileAlt,
      content: [
        "In3D.AI provides AI-powered 3D asset generation services.",
        "Users can create 3D models, characters, and environments from text prompts.",
        "Services include generation, storage, and export of 3D assets in various formats.",
        "We offer different subscription plans with varying generation limits and features."
      ]
    },
    {
      title: "User Accounts and Registration",
      icon: FaUserCheck,
      content: [
        "You must register for an account to access our services.",
        "You are responsible for maintaining the confidentiality of your account credentials.",
        "You must provide accurate and complete information during registration.",
        "You are responsible for all activities that occur under your account.",
        "You must notify us immediately of any unauthorized use of your account."
      ]
    },
    {
      title: "Acceptable Use Policy",
      icon: FaGavel,
      content: [
        "You may not use our services for any illegal or unauthorized purpose.",
        "You may not generate content that is harmful, offensive, or violates intellectual property rights.",
        "You may not attempt to reverse engineer or hack our systems.",
        "You may not use our services to generate content for commercial purposes without proper licensing.",
        "You must comply with all applicable laws and regulations."
      ]
    },
    {
      title: "Intellectual Property Rights",
      icon: FaShieldAlt,
      content: [
        "You retain ownership of the 3D assets you generate using our services.",
        "We retain ownership of our platform, technology, and AI models.",
        "You grant us a license to use your generated content for service improvement.",
        "You may not claim ownership of our AI technology or underlying algorithms.",
        "You are responsible for ensuring you have rights to any content you use as prompts."
      ]
    },
    {
      title: "Payment and Subscription",
      icon: FaHandshake,
      content: [
        "Subscription fees are billed in advance on a recurring basis.",
        "We offer a 30-day money-back guarantee for new subscriptions. See our Refund Policy for details.",
        "All payments are non-refundable after the 30-day guarantee period except as required by law.",
        "We may change our pricing with 30 days notice to existing subscribers.",
        "You may cancel your subscription at any time through your account settings.",
        "Unused generation credits do not carry over to the next billing period."
      ]
    },
    {
      title: "Limitation of Liability",
      icon: FaExclamationTriangle,
      content: [
        "Our services are provided 'as is' without warranties of any kind.",
        "We are not liable for any indirect, incidental, or consequential damages.",
        "Our total liability is limited to the amount you paid for our services in the past 12 months.",
        "We are not responsible for any content generated by our AI systems.",
        "You use our services at your own risk."
      ]
    },
    {
      title: "Termination",
      icon: FaBalanceScale,
      content: [
        "We may terminate or suspend your account for violation of these terms.",
        "You may terminate your account at any time by contacting support.",
        "Upon termination, your access to services will cease immediately.",
        "We may retain certain data as required by law or for business purposes.",
        "Termination does not affect any provisions that should survive."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white">
      {/* Header Section */}
      <section className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 mb-6">
              <FaFileAlt className="text-cyan-400 mr-2" />
              <span className="text-cyan-400 text-sm font-medium">Legal Terms</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Terms & Conditions
            </h1>
            
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto leading-relaxed">
              Please read these terms carefully before using our services. 
              These terms govern your use of In3D.AI and its features.
            </p>
            
            <p className="text-sm text-gray-400">
              Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700/50 hover:border-cyan-500/30 transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                    <section.icon className="text-2xl text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">{section.title}</h2>
                </div>
                
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-3 text-gray-300">
                      <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Important Notice */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-16 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-2xl p-8 border border-orange-500/30"
          >
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center mx-auto mb-6">
                <FaExclamationTriangle className="text-2xl text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-white mb-4">Important Notice</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                These terms constitute a legally binding agreement between you and Evoneural Artificial Intelligence OPC Private Limited. 
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these terms.
              </p>
              
              <div className="text-gray-400 text-sm">
                <p><strong className="text-white">Governing Law:</strong> These terms are governed by the laws of India.</p>
                <p><strong className="text-white">Dispute Resolution:</strong> Any disputes will be resolved through arbitration in Jaipur, India.</p>
              </div>
            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-8 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-2xl p-8 border border-cyan-500/30"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Questions About These Terms?</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                If you have any questions about these Terms & Conditions, please contact our legal team.
              </p>
              
              <div className="space-y-3 text-gray-400">
                <p><strong className="text-white">Email:</strong> legal@in3d.ai</p>
                <p><strong className="text-white">WhatsApp:</strong> +91 7023310122</p>
                <p><strong className="text-white">Address:</strong> Third Floor, Bhamashah Technohub, Sansthan Path, Malviya Nagar, Jaipur</p>
              </div>
            </div>
          </motion.div>

          {/* Additional Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-8 text-center"
          >
            <p className="text-gray-400 text-sm">
              These terms are effective as of {lastUpdated}. We encourage you to review these terms periodically 
              as they may be updated to reflect changes in our services or legal requirements.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;